import { parseNumericValue } from "./compareFieldUtils";
import { getSpecValue } from "./productSpecs";

const REASON_MAX = 500;
const COMPARATIVE =
  /\b(but|whereas|however|instead of|compared (?:with|to)|versus|vs\.?|while the|rather than)\b/i;

const SCREEN_KEYS = [
  "screen size",
  "screen_size",
  "display size",
  "display_size",
  "screen",
];

const OTHER_SPEC_KEYS = [
  { key: "ram", label: "RAM" },
  { key: "storage", label: "Storage" },
  { key: "battery", label: "Battery" },
  { key: "resolution", label: "Resolution" },
  { key: "weight", label: "Weight" },
  { key: "print_speed", label: "Print speed" },
  { key: "connectivity", label: "Connectivity" },
];

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function idOf(product) {
  return String(product?._id || product?.id || "");
}

function money(amount) {
  return `$${Number(amount).toLocaleString()}`;
}

function readBudgetMax(searchParams) {
  const budget = searchParams?.budget;
  const max = budget && typeof budget === "object" ? budget.max : budget;
  const amount = Number(max);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function readUseCase(searchParams) {
  const raw =
    searchParams?.useCase ||
    searchParams?.primaryUseCase ||
    searchParams?.usage ||
    "";
  const text = String(raw).trim();
  if (!text || text.length > 48) return "";
  return text.toLowerCase();
}

function brandToken(name) {
  const token = String(name || "")
    .trim()
    .split(/\s+/)[0]
    .replace(/[^A-Za-z0-9-]/g, "");
  if (token.length < 3 || !/^[A-Za-z]/.test(token)) return "";
  return token;
}

function mentionsName(text, name, productName) {
  const full = String(name || "").trim();
  if (!full) return false;
  const lower = String(text || "").toLowerCase();
  if (lower.includes(full.toLowerCase())) return true;
  const brand = brandToken(name);
  const ownBrand = brandToken(productName);
  if (!brand || brand.toLowerCase() === ownBrand.toLowerCase()) return false;
  return new RegExp(`\\b${escapeRegExp(brand)}\\b`, "i").test(text);
}

function foreignMention(text, productName) {
  const own = String(productName || "").trim().toLowerCase();
  const pattern =
    /\b(?:with|but|than|versus|vs\.?|instead of)\s+the\s+([A-Z][\w-]+(?:\s+[A-Z0-9][\w-]*){0,3})/g;
  for (const match of String(text || "").matchAll(pattern)) {
    const mentioned = match[1].toLowerCase();
    if (!own || (!own.includes(mentioned) && !mentioned.includes(own))) {
      return true;
    }
  }
  return false;
}

function mentionsOthers(text, productName, otherNames = []) {
  const names = otherNames.filter(Boolean);
  if (names.some((name) => mentionsName(text, name, productName))) return true;
  if (!names.length && foreignMention(text, productName)) return true;
  return false;
}

function isComparative(text, productName, otherNames) {
  return (
    COMPARATIVE.test(text) ||
    mentionsOthers(text, productName, otherNames) ||
    foreignMention(text, productName)
  );
}

function ensurePeriod(text) {
  const trimmed = String(text || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!trimmed) return "";
  const sentence = /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function firstSentence(text) {
  const trimmed = String(text || "").trim();
  const match = trimmed.match(/^.+?[.!?](?=\s|$)/);
  return (match ? match[0] : trimmed).trim();
}

function extractOwnSentence(text, productName, otherNames) {
  const chunks = String(text || "")
    .split(/(?<=[.!?])\s+|\s+but\s+/i)
    .map((part) => part.trim())
    .filter(Boolean);

  for (const part of chunks) {
    if (COMPARATIVE.test(part)) continue;
    if (mentionsOthers(part, productName, otherNames)) continue;
    if (foreignMention(part, productName)) continue;
    if (part.length < 12) continue;
    return ensurePeriod(part);
  }
  return "";
}

function generateReason({ product, searchParams, role }) {
  const useCase = readUseCase(searchParams);
  const budgetMax = readBudgetMax(searchParams);
  const price = Number(product?.price);
  const underBudget =
    budgetMax != null && Number.isFinite(price) && price <= budgetMax;
  const budgetPhrase =
    budgetMax == null
      ? ""
      : underBudget
        ? ` under your ${money(budgetMax)} budget`
        : ` around your ${money(budgetMax)} budget`;
  const usePhrase = useCase ? ` for ${useCase}` : "";

  if (role === "value") {
    if (budgetPhrase || usePhrase) {
      return `Best price and value${budgetPhrase}${usePhrase}.`;
    }
    return "Best price and value of the options that fit.";
  }
  if (role === "top") {
    if (usePhrase || budgetPhrase) {
      return `Strongest match${usePhrase}${budgetPhrase}.`;
    }
    return "Strongest overall match for what you asked for.";
  }
  if (budgetPhrase || usePhrase) return `A strong fit${budgetPhrase}${usePhrase}.`;
  return "A strong fit for what you asked for.";
}

export function decisionPickRole(productId, { topProduct, valueProduct } = {}) {
  const id = String(productId || "");
  if (!id) return null;
  if (topProduct && idOf(topProduct) === id) return "top";
  if (valueProduct && idOf(valueProduct) === id) return "value";
  return null;
}

/**
 * One sentence about the chosen product. Clean coach advice is kept.
 * Comparative advice is replaced with a sentence from the pick's role,
 * budget, and use case, or with a clause that does not name another product.
 */
export function buildPickReason({
  product,
  advice = "",
  searchParams = null,
  otherNames = [],
  role = null,
} = {}) {
  const productName = product?.name || "";
  const source = String(advice || "").trim();
  const hasContext = Boolean(
    role || readBudgetMax(searchParams) || readUseCase(searchParams),
  );

  if (!source) {
    return generateReason({ product, searchParams, role }).slice(0, REASON_MAX);
  }

  if (!isComparative(source, productName, otherNames)) {
    return ensurePeriod(firstSentence(source)).slice(0, REASON_MAX);
  }

  if (hasContext) {
    return generateReason({ product, searchParams, role }).slice(0, REASON_MAX);
  }

  const extracted = extractOwnSentence(source, productName, otherNames);
  if (extracted && !isComparative(extracted, productName, otherNames)) {
    return extracted.slice(0, REASON_MAX);
  }

  return generateReason({ product, searchParams, role }).slice(0, REASON_MAX);
}

function cellText(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    return String(value.display || value.value || value.text || "");
  }
  return String(value);
}

function readInches(value, { bareNumber = false } = {}) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = cellText(value).trim();
  const labeled = text.match(
    /(\d+(?:\.\d+)?)\s*(?:-?\s*inch(?:es)?|"|”|''|\bin\b)/i,
  );
  if (labeled) return Number(labeled[1]);
  if (bareNumber && /^\d+(?:\.\d+)?$/.test(text)) return Number(text);
  return null;
}

function formatInches(inches) {
  const rounded = Number.isInteger(inches) ? String(inches) : String(inches);
  return `${rounded}-inch`;
}

function readScreen(product, table) {
  for (const key of SCREEN_KEYS) {
    const value = getSpecValue(product, key);
    const inches = readInches(value, { bareNumber: true });
    if (inches) return inches;
  }

  if (!table?.columns || !table?.rows) return null;
  const index = table.columns.findIndex(
    (column) => String(column.id) === idOf(product),
  );
  if (index < 0) return null;
  const row = table.rows.find((entry) =>
    /screen|display size/i.test(String(entry.label || entry.key || "")),
  );
  return readInches(row?.values?.[index], { bareNumber: true });
}

function readSpec(product, table, key, label) {
  const direct = getSpecValue(product, key);
  if (direct) return cellText(direct);
  if (!table?.columns || !table?.rows) return "";
  const index = table.columns.findIndex(
    (column) => String(column.id) === idOf(product),
  );
  if (index < 0) return "";
  const row = table.rows.find((entry) => {
    const name = String(entry.label || entry.key || "").toLowerCase();
    return name === key.toLowerCase() || name === label.toLowerCase();
  });
  return cellText(row?.values?.[index]);
}

function describeDifference(product, topProduct, table) {
  const chosenScreen = readScreen(product, table);
  const topScreen = readScreen(topProduct, table);
  if (chosenScreen && topScreen && chosenScreen !== topScreen) {
    const word = chosenScreen < topScreen ? "smaller" : "larger";
    return `with a ${word} ${formatInches(chosenScreen)} screen`;
  }

  for (const { key, label } of OTHER_SPEC_KEYS) {
    const value = readSpec(product, table, key, label);
    const topValue = readSpec(topProduct, table, key, label);
    if (!value || !topValue || value === topValue) continue;
    const numericA = parseNumericValue(value);
    const numericB = parseNumericValue(topValue);
    if (numericA != null && numericB != null && numericA !== numericB) {
      return numericA < numericB
        ? `with less ${label} (${value})`
        : `with more ${label} (${value})`;
    }
    return `with ${label} ${value}`;
  }

  return "";
}

function withThe(name) {
  const text = String(name || "").trim();
  if (!text) return "";
  if (/^the\s+/i.test(text)) return text;
  return `the ${text}`;
}

/**
 * Plain trade-off against the top match. Empty when this product is the
 * top match, so the receipt can hide the line.
 */
export function buildDecisionTradeoff(product, topProduct, table = null) {
  if (!product || !topProduct) return "";
  const chosenId = idOf(product);
  const topId = idOf(topProduct);
  if (!chosenId || chosenId === topId) return "";

  const titled = withThe(topProduct.name);
  if (!titled) return "";

  let priceClause = "";
  const chosenPrice = Number(product.price);
  const topPrice = Number(topProduct.price);
  if (
    Number.isFinite(chosenPrice) &&
    Number.isFinite(topPrice) &&
    chosenPrice !== topPrice
  ) {
    const diff = Math.abs(chosenPrice - topPrice);
    priceClause =
      chosenPrice < topPrice
        ? `${money(diff)} less than ${titled}`
        : `${money(diff)} more than ${titled}`;
  }

  const difference = describeDifference(product, topProduct, table);
  if (priceClause && difference) return `${priceClause}, ${difference}`;
  if (priceClause) return priceClause;
  if (!difference) return "";
  const rest = difference.replace(/^with a\s+/i, "").replace(/^with\s+/i, "");
  return `${rest.charAt(0).toUpperCase()}${rest.slice(1)} than ${titled}`;
}

export function presentTradeoff(tradeoff) {
  const text = String(tradeoff || "").trim();
  if (!text || /#\s*1\b/.test(text)) return "";
  return text;
}
